import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { ArrowLeft, CircleAlert, Shield, Trash2, MoreVertical } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { useMsal } from '@azure/msal-react';
import { ticketsApi } from '@/features/tickets/ticketsApi';
import { RichTextEditor, type RichTextEditorChange } from '@/features/tickets/RichTextEditor';
import { RichTextDisplay } from '@/features/tickets/RichTextDisplay';
import { useTicketEvents } from '@/features/tickets/useTicketEvents';
import { TicketItControls } from '@/features/tickets/TicketItControls';
import { WatchersPanel } from '@/features/tickets/WatchersPanel';
import { UserAvatar } from '@/features/users/UserAvatar';
import { useMe } from '@/features/me/useMe';
import { useAttachmentUploads } from '@/features/attachments/useAttachmentUploads';
import { AttachmentDropzone } from '@/features/attachments/AttachmentDropzone';
import { AttachmentList } from '@/features/attachments/AttachmentList';
import { attachmentsApi } from '@/features/attachments/attachmentsApi';
import type { Attachment, TicketDetail as TicketDetailDto, TicketMessageKind } from '@/features/tickets/schema';
import type { ApiError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';

/**
 * /tickets/:ticketNumber — full ticket + forum-style thread + reply composer.
 *
 * The mockup's "right rail" (status/assignee/category sidebar) is deferred
 * to a follow-up slice; this version focuses on the read-and-reply primary path.
 */
export function TicketDetail() {
  const params = useParams<{ ticketNumber: string }>();
  const ticketNumber = Number(params.ticketNumber);
  const getAccessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { accounts } = useMsal();
  const callerEmail = accounts[0]?.username ?? '';
  const me = useMe();
  const isIT = me.data?.role === 'ITAgent' || me.data?.role === 'ITAdmin';
  const isAdmin = me.data?.role === 'ITAdmin';
  const navigate = useNavigate();

  const [editorState, setEditorState] = useState<RichTextEditorChange | null>(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const replyAttachments = useAttachmentUploads();
  // Inline images pasted into the editor (uploaded immediately; their ids must be
  // associated with the reply alongside the dropzone files).
  const [inlineImageIds, setInlineImageIds] = useState<string[]>([]);
  const [inlinePending, setInlinePending] = useState(0);
  // @-mentioned user ids reported by the editor (IT-only — see below).
  const [mentionedIds, setMentionedIds] = useState<string[]>([]);
  // IT can post an internal note (IT-only, never emailed) instead of a public reply.
  const [kind, setKind] = useState<TicketMessageKind>('PublicReply');
  const isInternal = kind === 'InternalNote';

  const ticket = useQuery({
    queryKey: ['tickets', 'detail', ticketNumber],
    queryFn: ({ signal }) => ticketsApi.getDetail(ticketNumber, getAccessToken, signal),
    enabled: Number.isFinite(ticketNumber) && ticketNumber > 0
  });

  // IT @-mention candidates = requester + watchers + all IT users. Loaded lazily
  // for IT only (the endpoint is RequireIT) and cached for the session.
  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: ({ signal }) => ticketsApi.listAgents(getAccessToken, signal),
    enabled: isIT,
    staleTime: 5 * 60_000
  });
  const mentionCandidates = useMemo(() => {
    if (!isIT || !ticket.data) return undefined;
    const list: { id: string; entraObjectId: string; displayName: string; email: string }[] = [];
    const seen = new Set<string>();
    const add = (id: string, entraObjectId: string, displayName: string, email: string) => {
      if (seen.has(id)) return;
      seen.add(id);
      list.push({ id, entraObjectId, displayName, email });
    };
    add(ticket.data.requesterId, ticket.data.requesterEntraObjectId, ticket.data.requesterDisplayName, ticket.data.requesterEmail);
    for (const w of ticket.data.watchers) add(w.id, w.entraObjectId, w.displayName, w.email);
    for (const a of agents.data ?? []) add(a.id, a.entraObjectId, a.displayName, a.email);
    return list;
  }, [isIT, ticket.data, agents.data]);

  const reply = useMutation({
    mutationFn: () => {
      if (!ticket.data || !editorState || editorState.isEmpty) {
        throw new Error('Type a message before sending.');
      }
      return ticketsApi.postMessage(
        ticket.data.id,
        {
          bodyJson: editorState.bodyJson,
          bodyHtml: editorState.bodyHtml,
          bodyText: editorState.bodyText,
          kind,
          attachmentIds: [...replyAttachments.ids, ...inlineImageIds],
          // Mentions narrow the recipient list on a public reply (IT-only on the server).
          mentionedUserIds: kind === 'PublicReply' && isIT && mentionedIds.length > 0 ? mentionedIds : undefined
        },
        getAccessToken
      );
    },
    onSuccess: () => {
      // Optimistically clear the editor + refresh the thread. Reset to a public
      // reply so the next message isn't accidentally posted as internal.
      setResetSignal((n) => n + 1);
      setEditorState(null);
      setKind('PublicReply');
      replyAttachments.reset();
      setInlineImageIds([]);
      setMentionedIds([]);
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'detail', ticketNumber] });
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'mine'] }); // updatedUtc moved
    }
  });

  const deleteTicket = useMutation({
    mutationFn: () => {
      if (!ticket.data) throw new Error('Ticket not loaded.');
      return ticketsApi.deleteTicket(ticket.data.id, getAccessToken);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'queue'] });
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'mine'] });
      void navigate('/queue', { replace: true });
    }
  });

  // Live updates: subscribe to this ticket's SSE stream once we know its id, so
  // a reply posted elsewhere (IT console, another tab, Outlook sync later)
  // refreshes the thread without a manual reload.
  useTicketEvents(ticket.data?.id, ticketNumber);

  if (!Number.isFinite(ticketNumber) || ticketNumber <= 0) {
    return <NotFound number={params.ticketNumber} />;
  }

  if (ticket.isPending) return <DetailSkeleton />;

  if (ticket.isError) {
    const status = (ticket.error as ApiError)?.status;
    if (status === 404) return <NotFound number={String(ticketNumber)} />;
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2">
          <CircleAlert className="w-4 h-4 mt-0.5" />
          <span>Couldn't load this ticket: {(ticket.error as Error).message}</span>
        </div>
      </div>
    );
  }

  const t = ticket.data;
  const isRequester = t.requesterEmail.localeCompare(callerEmail, undefined, { sensitivity: 'accent' }) === 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="mb-4">
        <Link to="/tickets" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Back to My Tickets
        </Link>
      </div>

      <header className="mb-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
              <span className="font-mono">#{t.ticketNumber}</span>
              <span>•</span>
              <span>Opened {format(new Date(t.createdUtc), 'PPpp')}</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold break-words">{t.subject}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <UserAvatar oid={t.requesterEntraObjectId} name={t.requesterDisplayName} sizeClass="w-6 h-6" />
              <span>
                Opened by <span className="text-foreground font-medium">{t.requesterDisplayName}</span>
                <span className="hidden sm:inline"> · {t.requesterEmail}</span>
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <StatusBadge status={t.status} />
              <CategoryBadge category={t.category} application={t.application} />
            </div>
          </div>

          {isAdmin && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label="Ticket actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
              {menuOpen && (
                <>
                  {/* Click-away backdrop */}
                  <button
                    type="button"
                    aria-hidden
                    tabIndex={-1}
                    onClick={() => setMenuOpen(false)}
                    className="fixed inset-0 z-10 cursor-default"
                  />
                  <div
                    role="menu"
                    className="absolute right-0 z-20 mt-1 w-44 rounded-md border bg-card shadow-md py-1 text-sm"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      disabled={deleteTicket.isPending}
                      onClick={() => {
                        setMenuOpen(false);
                        if (confirm(`Permanently delete ticket #${t.ticketNumber}? This removes the whole thread and can't be undone.`)) {
                          deleteTicket.mutate();
                        }
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-destructive hover:bg-destructive/10 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" /> {deleteTicket.isPending ? 'Deleting…' : 'Delete ticket'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </header>

      {isIT && (
        <TicketItControls
          ticketId={t.id}
          ticketNumber={t.ticketNumber}
          assigneeId={t.assigneeId}
          status={t.status}
          autoCloseArmed={t.autoCloseArmed}
          autoCloseDueAt={t.autoCloseDueAt}
          category={t.category}
          application={t.application}
          priority={t.priority}
        />
      )}

      {(isIT || isRequester) && (
        <WatchersPanel ticketId={t.id} ticketNumber={t.ticketNumber} watchers={t.watchers} />
      )}

      {/* Original description = first "post" in the thread */}
      <Message
        author={t.requesterDisplayName}
        authorOid={t.requesterEntraObjectId}
        isYou={t.requesterEmail.localeCompare(callerEmail, undefined, { sensitivity: 'accent' }) === 0}
        kind="PublicReply"
        html={t.descriptionHtml}
        createdUtc={t.createdUtc}
        attachments={t.attachments}
        isOriginal
      />

      {/* Replies */}
      {t.messages.map((m) => (
        <Message
          key={m.id}
          author={m.authorDisplayName}
          authorOid={m.authorEntraObjectId}
          isYou={false /* refine with caller-oid comparison once /api/me lands properly */}
          kind={m.kind}
          html={m.bodyHtml}
          createdUtc={m.createdUtc}
          attachments={m.attachments}
        />
      ))}

      {/* Reply composer */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {isInternal ? 'Internal note' : 'Reply'}
          </div>
          {isIT && (
            <div className="inline-flex rounded-md border p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setKind('PublicReply')}
                className={cn('px-2.5 py-1 rounded', !isInternal && 'bg-primary text-primary-foreground')}
              >
                Public reply
              </button>
              <button
                type="button"
                onClick={() => setKind('InternalNote')}
                className={cn(
                  'px-2.5 py-1 rounded inline-flex items-center gap-1',
                  isInternal && 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                )}
              >
                <Shield className="w-3 h-3" /> Internal note
              </button>
            </div>
          )}
        </div>

        <div className={cn('rounded-md', isInternal && 'ring-1 ring-amber-500/40 bg-amber-500/5 p-1')}>
          <RichTextEditor
            onChange={setEditorState}
            resetSignal={resetSignal}
            placeholder={isInternal ? 'Internal note — visible to IT only, never emailed to the requester.' : 'Add a reply — IT will be notified. Paste a screenshot to embed it.'}
            minHeight={140}
            uploadImage={async (file) => {
              setInlinePending((n) => n + 1);
              try {
                const up = await attachmentsApi.upload(file, true, getAccessToken);
                return { id: up.id, url: up.url };
              } finally {
                setInlinePending((n) => n - 1);
              }
            }}
            onInlineImageAdded={(id) => setInlineImageIds((prev) => [...prev, id])}
            mentionCandidates={mentionCandidates}
            onMentionsChanged={setMentionedIds}
          />
        </div>

        <AttachmentDropzone
          items={replyAttachments.items}
          onAddFiles={replyAttachments.addFiles}
          onRemove={replyAttachments.remove}
          disabled={reply.isPending}
        />

        {isIT && !isInternal && mentionedIds.length > 0 && (
          <div className="mt-2 text-xs text-muted-foreground">
            Reply will email only the {mentionedIds.length} mentioned person{mentionedIds.length === 1 ? '' : 's'} — not the requester or watchers.
          </div>
        )}

        {reply.isError && (
          <div className="mt-2 text-sm text-destructive">{(reply.error as Error).message}</div>
        )}

        <div className="mt-3 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={reply.isPending || replyAttachments.uploading || inlinePending > 0 || !editorState || editorState.isEmpty}
            onClick={() => reply.mutate()}
            className={cn(
              'px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed',
              isInternal ? 'bg-amber-600 text-white' : 'bg-primary text-primary-foreground'
            )}
          >
            {reply.isPending ? 'Sending…' : replyAttachments.uploading ? 'Uploading…' : isInternal ? 'Add Internal Note' : 'Send Reply'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- small presentational components ------------------------------------

function Message({
  author,
  authorOid,
  isYou,
  kind,
  html,
  createdUtc,
  attachments,
  isOriginal
}: {
  author: string;
  authorOid: string;
  isYou: boolean;
  kind: TicketMessageKind;
  html: string;
  createdUtc: string;
  attachments?: Attachment[];
  isOriginal?: boolean;
}) {
  const isInternal = kind === 'InternalNote';
  return (
    <article
      className={cn(
        'rounded-xl border bg-card p-5 mb-3',
        isInternal && 'bg-amber-500/5 border-amber-500/30'
      )}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 text-sm min-w-0">
          <UserAvatar oid={authorOid} name={author} sizeClass="w-7 h-7" />
          <span className="font-medium truncate">{author}</span>
          {isYou && <span className="text-xs text-muted-foreground">(you)</span>}
          {isOriginal && <span className="text-xs text-muted-foreground rounded-full bg-muted px-2 py-0.5">Original request</span>}
          {isInternal && (
            <span className="text-xs text-amber-700 dark:text-amber-300 inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5">
              <Shield className="w-3 h-3" /> Internal note
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(createdUtc), { addSuffix: true })}</span>
      </div>
      <RichTextDisplay html={html} />
      {attachments && attachments.length > 0 && <AttachmentList attachments={attachments} />}
    </article>
  );
}

function StatusBadge({ status }: { status: TicketDetailDto['status'] }) {
  const labels: Record<typeof status, string> = {
    Pending: 'Pending',
    InProgress: 'In Progress',
    WaitingOnSomeone: 'Waiting on Someone',
    PendingSoftwareRelease: 'Pending Software Release',
    Completed: 'Completed'
  };
  return <span className="inline-flex items-center rounded-full bg-primary/15 text-primary px-2 py-0.5 text-xs font-medium">{labels[status]}</span>;
}

function CategoryBadge({ category, application }: { category: TicketDetailDto['category']; application: string | null }) {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs">
      {category}
      {application && <span className="opacity-70 ml-1">· {application}</span>}
    </span>
  );
}

function NotFound({ number }: { number: string | undefined }) {
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <Link to="/tickets" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to My Tickets
      </Link>
      <div className="rounded-xl border border-dashed bg-card p-10 text-center">
        <div className="text-base font-medium">Ticket {number ? `#${number}` : 'not found'}</div>
        <p className="text-sm text-muted-foreground mt-1.5">
          Either this ticket doesn't exist, or it belongs to someone else.
        </p>
      </div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto">
      <div className="h-3 w-32 bg-muted rounded animate-pulse mb-4" />
      <div className="h-7 w-3/4 bg-muted rounded animate-pulse mb-6" />
      <div className="rounded-xl border bg-card p-5 space-y-2">
        <div className="h-3 w-1/3 bg-muted rounded animate-pulse" />
        <div className="h-3 w-full bg-muted rounded animate-pulse" />
        <div className="h-3 w-5/6 bg-muted rounded animate-pulse" />
      </div>
    </div>
  );
}
